// TermsPage.js — Terms of Use for premierleaguescorigami.com. Short,
// readable, covers the acceptable-use and disclaimer boilerplate AdSense
// and similar programs expect to see alongside a privacy policy.

import React from "react";
import { Box, Container, Typography, Link } from "@mui/material";
import SEO from "./components/SEO";
import { BRAND } from "./theme";

const H2 = (props) => (
  <Typography
    component="h2"
    sx={{
      mt: 4,
      mb: 1.5,
      fontSize: { xs: "1.2rem", sm: "1.4rem" },
      fontWeight: 700,
      color: "#fff",
    }}
    {...props}
  />
);

const P = (props) => (
  <Typography
    sx={{
      color: BRAND.textSecondary,
      lineHeight: 1.8,
      fontSize: { xs: "0.95rem", sm: "1rem" },
      mb: 1.5,
    }}
    {...props}
  />
);

export default function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Use"
        description="Terms of use for Premier League Scorigami — acceptable use, disclaimers, data attribution, and trademark notices."
        path="/terms"
      />
      <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
        <Typography
          component="h1"
          sx={{
            background: BRAND.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "2rem", sm: "2.75rem" },
            fontWeight: 800,
            mb: 1,
          }}
        >
          Terms of Use
        </Typography>
        <Typography sx={{ color: BRAND.textSecondary, mb: 3 }}>
          Last updated: April 21, 2026
        </Typography>

        <P>
          These terms govern your use of Premier League Scorigami ("the
          site"). By accessing or using the site you agree to these terms.
          If you do not agree, please do not use the site.
        </P>

        <H2>What the Site Is</H2>
        <P>
          Premier League Scorigami is an independent, fan-made statistical
          visualization of English Premier League scorelines since the
          league's inception in 1992. It is not affiliated with, endorsed by,
          or sponsored by the Premier League, any club, any player, or any
          official rights-holder.
        </P>

        <H2>Acceptable Use</H2>
        <P>
          You may browse, link to, and share the site freely for personal and
          non-commercial purposes. You agree not to:
        </P>
        <Box component="ul" sx={{ color: BRAND.textSecondary, pl: 3, mb: 2 }}>
          <li>
            scrape, republish, or resell the data or visuals in bulk or for
            commercial gain;
          </li>
          <li>
            attempt to disrupt, probe, or interfere with the site's
            infrastructure or its underlying APIs;
          </li>
          <li>
            use the site to harass, defame, or mislead others, or in
            violation of any applicable law.
          </li>
        </Box>

        <H2>Data Accuracy and Disclaimers</H2>
        <P>
          Match data is derived from public fixtures and results and is
          provided on a best-effort basis. We do not guarantee that every
          scoreline, occurrence, or stat is free from error. The site is
          provided "as is" without warranties of any kind. To the fullest
          extent permitted by law, we are not liable for any loss or damage
          arising from your use of the site.
        </P>

        <H2>Trademarks</H2>
        <P>
          "Premier League" and related marks are the property of their
          respective owners. Club names, badges, and any other marks are
          used descriptively to refer to the matches they relate to, and no
          claim of ownership or official endorsement is made.
        </P>

        <H2>Third-Party Links and Ads</H2>
        <P>
          The site may link to or display content from third parties
          (for example, linking to the Premier League site, or serving
          advertising via Google AdSense). We are not responsible for the
          content, availability, or practices of those third parties. Your
          interactions with third-party services are governed by their own
          terms and policies.
        </P>

        <H2>Changes</H2>
        <P>
          We may update these terms from time to time. The revised terms
          take effect when they are posted on this page. Continued use of
          the site after an update constitutes acceptance of the revised
          terms.
        </P>

        <H2>Contact</H2>
        <P>
          Questions about these terms can be sent via the{" "}
          <Link href="/contact" sx={{ color: BRAND.cyan }}>Contact</Link> page.
        </P>
      </Container>
    </>
  );
}
